import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import Navbar from "@/components/Navbar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import axios from "axios";
import {
  Users,
  Heart,
  BookOpen,
  TrendingUp,
  ArrowLeft,
  Shield,
  Mail,
  Calendar,
  Target,
  CreditCard,
  BarChart3,
  Clock,
  UserPlus,
  Activity,
  Trash2,
  Ban,
  Unlock,
  Send,
  Bell,
  Eye,
  ChevronUp,
  ChevronDown,
  UserCog
} from "lucide-react";

const categoryLabels = {
  histoire: "Histoire", geographie: "Géographie", valeurs: "Valeurs",
  institutions: "Institutions", symboles: "Symboles", culture: "Culture",
  europe: "Europe", droits: "Droits", societe: "Société", demarches: "Démarches",
};
const categoryColors = {
  histoire: "bg-amber-500", geographie: "bg-emerald-500", valeurs: "bg-blue-500",
  institutions: "bg-purple-500", symboles: "bg-red-500", culture: "bg-pink-500",
  europe: "bg-indigo-500", droits: "bg-cyan-500", societe: "bg-orange-500", demarches: "bg-teal-500",
};

const Admin = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [donations, setDonations] = useState([]);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifForm, setNotifForm] = useState({ title: "", message: "" });
  const [sendingNotif, setSendingNotif] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!user || user.role !== "admin") { navigate("/dashboard"); return; }
    fetchData();
  }, [user, token]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, donationsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers }),
        axios.get(`${API}/admin/donations`, { headers }),
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setDonations(donationsRes.data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  const handleBlock = async (userId, block) => {
    try {
      await axios.post(`${API}/admin/user/block`, { user_id: userId, blocked: block }, { headers });
      toast.success(block ? "Utilisateur bloqué" : "Utilisateur débloqué");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDelete = async (userId, name) => {
    if (!window.confirm(`Supprimer définitivement ${name} ? Cette action est irréversible.`)) return;
    try {
      await axios.delete(`${API}/admin/user/${userId}`, { headers });
      toast.success("Utilisateur supprimé");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleChangeRole = async (userId, newRole) => {
    try {
      await axios.post(`${API}/admin/user/role`, { user_id: userId, role: newRole }, { headers });
      toast.success(`Rôle changé en ${newRole}`);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleViewUser = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/user/${userId}`, { headers });
      setSelectedUser(res.data);
    } catch { toast.error("Erreur chargement détails"); }
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notifForm.title || !notifForm.message) { toast.error("Remplissez tous les champs"); return; }
    setSendingNotif(true);
    try {
      await axios.post(`${API}/admin/notification`, { ...notifForm, target: "all" }, { headers });
      toast.success("Notification envoyée à tous les utilisateurs");
      setNotifForm({ title: "", message: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSendingNotif(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";
  const formatShortDate = (d) => d ? new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "—";

  if (loading) return (<div className="min-h-screen bg-slate-50"><Navbar /><div className="flex items-center justify-center h-[60vh]"><div className="spinner"></div></div></div>);

  const regDays = stats?.registrations_by_day || {};
  const sortedDays = Object.keys(regDays).sort().slice(-14);
  const maxReg = Math.max(1, ...sortedDays.map((d) => regDays[d]));
  const catCount = stats?.categories_count || {};
  const sortedCats = Object.entries(catCount).sort((a, b) => b[1] - a[1]);
  const maxCat = Math.max(1, ...sortedCats.map(([, v]) => v));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button variant="ghost" onClick={() => navigate("/dashboard")} className="mb-4 text-slate-600">
          <ArrowLeft className="h-4 w-4 mr-2" />Retour
        </Button>

        <div className="flex items-center gap-3 mb-8">
          <div className="h-10 w-10 bg-blue-900 rounded-lg flex items-center justify-center">
            <Shield className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-serif font-bold text-slate-900">Tableau de bord administrateur</h1>
            <p className="text-sm text-slate-500">Gestion complète de CiviquePrep</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: "dashboard", label: "Vue d'ensemble", icon: BarChart3 },
            { id: "users", label: `Utilisateurs (${users.length})`, icon: Users },
            { id: "donations", label: `Donations (${donations.length})`, icon: CreditCard },
            { id: "notifications", label: "Notifications", icon: Bell },
          ].map((tab) => (
            <Button key={tab.id} variant={activeTab === tab.id ? "default" : "outline"}
              onClick={() => { setActiveTab(tab.id); setSelectedUser(null); }}
              className={`shrink-0 ${activeTab === tab.id ? "bg-blue-900" : ""}`} size="sm" data-testid={`tab-${tab.id}`}>
              <tab.icon className="h-4 w-4 mr-2" />{tab.label}
            </Button>
          ))}
        </div>

        {/* ========== DASHBOARD ========== */}
        {activeTab === "dashboard" && stats && (
          <div className="space-y-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="border-l-4 border-l-blue-500"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs text-slate-500 uppercase tracking-wide">Utilisateurs</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats.users_count}</p></div><div className="h-12 w-12 bg-blue-100 rounded-xl flex items-center justify-center"><Users className="h-6 w-6 text-blue-600" /></div></div></CardContent></Card>
              <Card className="border-l-4 border-l-green-500"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs text-slate-500 uppercase tracking-wide">Quiz lancés</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats.quizzes_count}</p></div><div className="h-12 w-12 bg-green-100 rounded-xl flex items-center justify-center"><BookOpen className="h-6 w-6 text-green-600" /></div></div></CardContent></Card>
              <Card className="border-l-4 border-l-red-500"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs text-slate-500 uppercase tracking-wide">Dons reçus</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats.donations_count}</p>{stats.donations_pending > 0 && <p className="text-xs text-amber-600">{stats.donations_pending} en attente</p>}</div><div className="h-12 w-12 bg-red-100 rounded-xl flex items-center justify-center"><Heart className="h-6 w-6 text-red-500" /></div></div></CardContent></Card>
              <Card className="border-l-4 border-l-amber-500"><CardContent className="p-4"><div className="flex items-center justify-between"><div><p className="text-xs text-slate-500 uppercase tracking-wide">Total dons</p><p className="text-3xl font-bold text-slate-900 mt-1">{stats.total_donations.toFixed(2)}€</p></div><div className="h-12 w-12 bg-amber-100 rounded-xl flex items-center justify-center"><TrendingUp className="h-6 w-6 text-amber-600" /></div></div></CardContent></Card>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <Card><CardHeader className="pb-2"><CardTitle className="text-base font-serif flex items-center gap-2"><UserPlus className="h-4 w-4 text-blue-600" />Inscriptions (14 derniers jours)</CardTitle></CardHeader><CardContent>{sortedDays.length > 0 ? (<div className="flex items-end gap-1.5 h-40">{sortedDays.map((day) => { const count = regDays[day] || 0; const heightPct = Math.max(8, (count / maxReg) * 100); return (<div key={day} className="flex-1 flex flex-col items-center gap-1"><span className="text-xs font-semibold text-slate-700">{count}</span><div className="w-full bg-blue-500 rounded-t-md transition-all" style={{ height: `${heightPct}%` }} /><span className="text-[10px] text-slate-400">{formatShortDate(day)}</span></div>);})}</div>) : (<p className="text-slate-400 text-sm text-center py-8">Aucune donnée</p>)}</CardContent></Card>
              <Card><CardHeader className="pb-2"><CardTitle className="text-base font-serif flex items-center gap-2"><Activity className="h-4 w-4 text-green-600" />Quiz par catégorie</CardTitle></CardHeader><CardContent>{sortedCats.length > 0 ? (<div className="space-y-3">{sortedCats.map(([cat, count]) => (<div key={cat} className="flex items-center gap-3"><span className="text-xs text-slate-600 w-24 truncate">{categoryLabels[cat] || cat}</span><div className="flex-1 h-5 bg-slate-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${categoryColors[cat] || "bg-slate-400"}`} style={{ width: `${(count / maxCat) * 100}%` }} /></div><span className="text-xs font-semibold text-slate-700 w-8 text-right">{count}</span></div>))}</div>) : (<p className="text-slate-400 text-sm text-center py-8">Aucun quiz</p>)}</CardContent></Card>
            </div>
          </div>
        )}

        {/* ========== USERS ========== */}
        {activeTab === "users" && !selectedUser && (
          <Card data-testid="users-table">
            <CardHeader><CardTitle className="text-lg font-serif">Gestion des utilisateurs</CardTitle></CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-200 text-slate-500">
                    <th className="text-left py-3 px-2 font-medium">Nom</th>
                    <th className="text-left py-3 px-2 font-medium">Email</th>
                    <th className="text-left py-3 px-2 font-medium">Rôle</th>
                    <th className="text-left py-3 px-2 font-medium">Statut</th>
                    <th className="text-left py-3 px-2 font-medium">Quiz</th>
                    <th className="text-left py-3 px-2 font-medium">Actions</th>
                  </tr></thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2 font-medium text-slate-900">{u.name}</td>
                        <td className="py-3 px-2 text-slate-600 text-xs"><div className="flex items-center gap-1"><Mail className="h-3 w-3" />{u.email}</div></td>
                        <td className="py-3 px-2">
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${u.role === "admin" ? "bg-blue-100 text-blue-900" : "bg-slate-100 text-slate-600"}`}>
                            {u.role === "admin" && <Shield className="h-3 w-3" />}
                            {u.role === "admin" ? "Admin" : "Utilisateur"}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          {u.blocked ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Bloqué</span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Actif</span>
                          )}
                        </td>
                        <td className="py-3 px-2 text-slate-600">{u.progress?.total_quizzes || 0}</td>
                        <td className="py-3 px-2">
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleViewUser(u.id)} className="p-1.5 rounded hover:bg-blue-50 text-blue-600" title="Voir détails"><Eye className="h-4 w-4" /></button>
                            {u.role !== "admin" && (
                              <>
                                <button onClick={() => handleChangeRole(u.id, "admin")} className="p-1.5 rounded hover:bg-amber-50 text-amber-600" title="Promouvoir admin"><ChevronUp className="h-4 w-4" /></button>
                                {u.blocked ? (
                                  <button onClick={() => handleBlock(u.id, false)} className="p-1.5 rounded hover:bg-green-50 text-green-600" title="Débloquer"><Unlock className="h-4 w-4" /></button>
                                ) : (
                                  <button onClick={() => handleBlock(u.id, true)} className="p-1.5 rounded hover:bg-orange-50 text-orange-600" title="Bloquer"><Ban className="h-4 w-4" /></button>
                                )}
                                <button onClick={() => handleDelete(u.id, u.name)} className="p-1.5 rounded hover:bg-red-50 text-red-600" title="Supprimer"><Trash2 className="h-4 w-4" /></button>
                              </>
                            )}
                            {u.role === "admin" && u.id !== user.id && (
                              <button onClick={() => handleChangeRole(u.id, "user")} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Rétrograder"><ChevronDown className="h-4 w-4" /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* User Detail Modal */}
        {activeTab === "users" && selectedUser && (
          <Card data-testid="user-detail">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg font-serif flex items-center gap-2">
                  <UserCog className="h-5 w-5" />
                  Détail de {selectedUser.user?.name}
                </CardTitle>
                <Button variant="outline" size="sm" onClick={() => setSelectedUser(null)}>Fermer</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Email</p>
                  <p className="font-medium">{selectedUser.user?.email}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Rôle</p>
                  <p className="font-medium capitalize">{selectedUser.user?.role || "user"}</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-lg">
                  <p className="text-xs text-slate-500">Inscrit le</p>
                  <p className="font-medium">{formatDate(selectedUser.user?.created_at)}</p>
                </div>
              </div>

              {selectedUser.progress && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <p className="text-2xl font-bold text-blue-900">{selectedUser.progress.total_quizzes || 0}</p>
                    <p className="text-xs text-slate-500">Quiz</p>
                  </div>
                  <div className="text-center p-3 bg-green-50 rounded-lg">
                    <p className="text-2xl font-bold text-green-700">{selectedUser.progress.correct_answers || 0}</p>
                    <p className="text-xs text-slate-500">Bonnes réponses</p>
                  </div>
                  <div className="text-center p-3 bg-amber-50 rounded-lg">
                    <p className="text-2xl font-bold text-amber-700">{selectedUser.progress.total_questions || 0}</p>
                    <p className="text-xs text-slate-500">Questions tentées</p>
                  </div>
                  <div className="text-center p-3 bg-purple-50 rounded-lg">
                    <p className="text-2xl font-bold text-purple-700">
                      {selectedUser.progress.total_questions > 0 ? Math.round((selectedUser.progress.correct_answers / selectedUser.progress.total_questions) * 100) : 0}%
                    </p>
                    <p className="text-xs text-slate-500">Taux réussite</p>
                  </div>
                </div>
              )}

              <div>
                <h4 className="font-semibold text-slate-900 mb-3">Historique des quiz</h4>
                {selectedUser.quiz_history?.length > 0 ? (
                  <div className="space-y-2">
                    {selectedUser.quiz_history.map((q, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="text-xs font-medium text-slate-500 capitalize">{categoryLabels[q.category] || q.category}</span>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className={`font-semibold ${q.percentage >= 70 ? "text-green-600" : q.percentage >= 40 ? "text-amber-600" : "text-red-600"}`}>
                            {q.score}/{q.total} ({q.percentage}%)
                          </span>
                          <span className="text-xs text-slate-400">{formatDate(q.completed_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-400 text-sm">Aucun quiz complété</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ========== DONATIONS ========== */}
        {activeTab === "donations" && (
          <Card data-testid="donations-table">
            <CardHeader><CardTitle className="text-lg font-serif">Historique des donations</CardTitle></CardHeader>
            <CardContent>
              {donations.length === 0 ? (
                <p className="text-slate-500 text-center py-8">Aucune donation</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-200 text-slate-500">
                      <th className="text-left py-3 px-2 font-medium">Montant</th>
                      <th className="text-left py-3 px-2 font-medium">Méthode</th>
                      <th className="text-left py-3 px-2 font-medium">Statut</th>
                      <th className="text-left py-3 px-2 font-medium">Package</th>
                      <th className="text-left py-3 px-2 font-medium">Date</th>
                    </tr></thead>
                    <tbody>
                      {donations.map((d, i) => (
                        <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-2 font-semibold text-slate-900">{d.amount?.toFixed(2)}€</td>
                          <td className="py-3 px-2"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${d.method === "paypal" ? "bg-blue-100 text-[#0070ba]" : "bg-purple-100 text-purple-700"}`}>{d.method === "paypal" ? "PayPal" : "Stripe"}</span></td>
                          <td className="py-3 px-2"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${d.payment_status === "paid" ? "bg-green-100 text-green-700" : d.payment_status === "pending" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>{d.payment_status === "paid" ? "Payé" : d.payment_status === "pending" ? "En attente" : d.payment_status}</span></td>
                          <td className="py-3 px-2 text-slate-600 capitalize">{d.metadata?.package_id || "—"}</td>
                          <td className="py-3 px-2 text-slate-500 text-xs">{formatDate(d.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* ========== NOTIFICATIONS ========== */}
        {activeTab === "notifications" && (
          <Card data-testid="notifications-panel">
            <CardHeader><CardTitle className="text-lg font-serif flex items-center gap-2"><Bell className="h-5 w-5 text-amber-500" />Envoyer une notification</CardTitle></CardHeader>
            <CardContent>
              <form onSubmit={handleSendNotification} className="space-y-4 max-w-lg">
                <div className="space-y-2">
                  <Label>Titre</Label>
                  <Input
                    placeholder="Titre de la notification"
                    value={notifForm.title}
                    onChange={(e) => setNotifForm({ ...notifForm, title: e.target.value })}
                    data-testid="notif-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Message</Label>
                  <textarea
                    className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring min-h-[100px]"
                    placeholder="Contenu du message..."
                    value={notifForm.message}
                    onChange={(e) => setNotifForm({ ...notifForm, message: e.target.value })}
                    data-testid="notif-message"
                  />
                </div>
                <p className="text-xs text-slate-500">La notification sera envoyée à tous les utilisateurs.</p>
                <Button type="submit" disabled={sendingNotif} className="bg-blue-900 hover:bg-blue-800" data-testid="send-notif-btn">
                  <Send className="h-4 w-4 mr-2" />
                  {sendingNotif ? "Envoi..." : "Envoyer la notification"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </main>
    </div>
  );
};

export default Admin;
